import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { User } from 'src/app/model/user';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';

type Permission = {id: number | null, permission: string};

@Component({
  selector: 'app-users-page',
  templateUrl: './users-page.component.html',
  styleUrls: ['./users-page.component.scss']
})
export class UsersPageComponent {
  /** List of all users to render */
  users: User[] = [];

  /** Associated user forms for actions */
  userForms: FormGroup[] = [];

  /** Available permissions the user can add */
  permissions: string[] = [];

  /**
   * Default constructor to initialize services
   * 
   * @param http for http requests
   * @param fb for form building
   */
  constructor(
    private _http: HttpClient,
    private _fb: FormBuilder,
    private _userService: UserService,
    private _toastrService: ToastrService,
    private _dialogService: DialogService,
  ) { }

  /**
   * Fetch users from the API and initialize forms 
   */
  ngOnInit(): void {
    this._fetch();
  }

  /**
   * Update users and permissions when the user changes
   */
  private _fetch(): void {
    // Get the list of users from the API
    this._http.get<User[]>(environment.baseUrl + "/users").subscribe({
      next: (data) => {
        // Remove the current user from the list
        this.users = data.filter(x => x.email !== this._userService.user?.email);

        // Initialize forms for each user
        this.userForms = this.users.map(user =>
          this._fb.group({
            id: [user.id],
            name: [user.name],
            email: [user.email],
            permissions: this._fb.array(user.permissions.map(x => this._fb.control(x.permission))),
          })
        );
      },
      error: (err) => {
        console.error('[ERR]: Error fetching users', err);
      }
    });

    // Get the list of permissions from the current user
    this._http.get<string[]>(environment.baseUrl + "/users/permissions").subscribe({
      next: (data) => {
        this.permissions = data;
      },
      error: (err) => {
        console.error('[ERR]: Error fetching permissions', err);
      }
    });
  }

  /**
   * Get permissions from a form group
   * 
   * @param form FormGroup to get the permissions array from
   * @returns Permissions from the form group
   */
  getPermissionsArray(form: FormGroup): FormArray {
    return form.get('permissions') as FormArray;
  }

  /**
   * Add a new permission to the form group
   * 
   * @param form FormGroup to add permissions to 
   */
  addPermission(form: FormGroup): void {
    this.getPermissionsArray(form).push(this._fb.control(''));
  }

  /**
   * Check if the user has all available permissions
   * 
   * @param form FormGroup to check permissions for
   * @returns True if user has all permissions, false otherwise
   */
  hasAllPermissions(form: FormGroup): boolean {
    const userPermissions = this.getPermissionsArray(form).value as string[];
    return userPermissions.length >= this.permissions.length;
  }

  /**
   * Get available permissions for a user (excluding already assigned ones)
   * 
   * @param form FormGroup to get available permissions for
   * @returns Array of available permissions
   */
  getAvailablePermissions(form: FormGroup): string[] {
    const userPermissions = this.getPermissionsArray(form).value as string[];
    return this.permissions.filter(permission => !userPermissions.includes(permission));
  }

  /**
   * Get permission options for a specific select dropdown
   * Includes available permissions plus the currently selected permission
   * 
   * @param form FormGroup containing the permissions
   * @param index Index of the permission in the FormArray
   * @returns Array of permission options for the select dropdown
   */
  getPermissionOptionsForSelect(form: FormGroup, index: number): string[] {
    const permissionsArray = this.getPermissionsArray(form);
    const currentValue = permissionsArray.at(index).value;
    const userPermissions = permissionsArray.value as string[];
    
    // Get permissions that are not assigned to other dropdowns
    const otherUserPermissions = userPermissions.filter((_, i) => i !== index);
    const availablePermissions = this.permissions.filter(permission => !otherUserPermissions.includes(permission));
    
    return availablePermissions;
  }

  /**
   * Remove a permission from the form group 
   * 
   * @param form FormGroup to remove permissions from
   * @param index Index to remove
   */
  removePermission(form: FormGroup, index: number): void {
    this.getPermissionsArray(form).removeAt(index);
  }

  /**
   * Save a user's permissions by sending a PUT request to the API
   * 
   * @param form FormGroup to save
   */
  saveUser(form: FormGroup): void {
    // Update user permissions to hold null id values
    const user: any = form.value;
    user.permissions = Array.from(new Set(user.permissions));
    user.permissions = user.permissions.map((x: string) => {
      return {id: null, permission: x}
    });
    console.log(user);

    // Save the user by sending a PUT request to the API
    this._http.put(environment.baseUrl + `/users/${user.id}`, user).subscribe({
      next: () => {
        console.log('[LOG]: User saved successfully!');
        this._toastrService.success("User saved successfully!");
      },
      error: (err) => {
        console.error('[ERR]: Error saving user', err);
      }
    });
  }

  /**
   * Open up a dialog to ask the user for a permission group to add to the provided user's
   * ID. Once the user selects a group, the group's permissions will be set using the API
   * 
   * @param id User ID to add the permission group to
   */
  assignGroup(form: FormGroup): void {
    const name: string = form.get("name")?.value || "user";

    const id = form.get("id")?.value;
    if (id == undefined) return;

    // Open the dialog
    this._dialogService.openPermissionGroupDialog(name).subscribe({
      next: (group: string | null) => {
        if (!group) return;

        // Send a POST request to the API to assign the group to the user
        this._http.post<User>(environment.baseUrl + `/users/group/${id}`, group).subscribe({
          next: (updated: User) => {
            console.log('[LOG]: User permissions assigned successfully!');
            this._toastrService.success("Group added successfully!");

            // Update the user in the list of user forms
            for (let i = 0; i < this.userForms.length; i++) {
              if (this.userForms[i].get('id')?.value === updated.id) {
                const permissionsArray = this.getPermissionsArray(this.userForms[i]);

                // Clear the current permissions
                while (permissionsArray.length) {
                  permissionsArray.removeAt(0);
                }

                // Add the updated permissions
                updated.permissions.forEach(x => {
                  permissionsArray.push(this._fb.control(x.permission));
                });

                break;
              }
            }
          },
          error: (err) => {
            console.error('[ERR]: Error assigning user permissions', err);
          }
        });
      },
    });
  }
}
